import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthApiService } from '@thehubz/client/data-access-api';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserSession } from '../models';
import { USER_SESSION_TOKEN } from '../user-access/user-session.token';
import { CommonUtilityLib, StorageUtilityLib } from '../util';
import { CheckSessionService } from './check-session.service';

@Injectable({ providedIn: 'root' })
export class UserSessionService {
  private loggedInSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    @Inject(USER_SESSION_TOKEN) private userSessionToken: string,
    private storageUtility: StorageUtilityLib,
    private router: Router,
    private commonUtilityLib: CommonUtilityLib,
    private authService: AuthApiService,
    private checkSessionService: CheckSessionService,
  ) {
    const session = this.getUserSession();
    if (session && !checkSessionService.isSessionExpired(session)) {
      this.setLoggedInState(true);
    }
  }

  getUserSession() {
    return this.storageUtility.getLocalStorageNullable<UserSession>(this.userSessionToken);
  }

  logoutUser(impersonated?: boolean, redirectUrl?: string, expired?: boolean) {
    this.authService.authControllerLogout(impersonated).subscribe({
      complete: () => {
        this.commonUtilityLib.routingObjectSetter({});
        this.checkSessionService.defaultLogoutRoutine(false);
        this.checkSessionService.stop();

        if (redirectUrl) {
          const navigate = redirectUrl.startsWith('/') ? redirectUrl : '/' + redirectUrl;
          this.router.navigateByUrl(navigate);
        } else {
          this.router
            .navigate(['/login'], {
              queryParamsHandling: 'merge',
              queryParams: expired ? { sessionExpired: true } : {},
            })
            .then(() => {
              window.location.reload(); // brut force to reload the app
            });
        }
      },
      error: () => {
        this.checkSessionService.defaultLogoutRoutine(false);
        this.checkSessionService.stop();
        this.router.navigate(['/login']).then(() => {
          window.location.reload();
        });
      },
    });
  }

  setLoggedInState(isLoggedIn: boolean) {
    if (this.loggedInSubject.value !== isLoggedIn) {
      this.loggedInSubject.next(isLoggedIn);
    }
  }

  getLoggedInState(): Observable<boolean> {
    return this.loggedInSubject.asObservable();
  }
}
